import { useRecoilState, useRecoilValue } from 'recoil'
import { useNavigate } from 'react-router-dom'
import {
  userAtom,
  authTokenAtom,
  branchesAtom,
  selectedBranchAtom,
  environmentAtom,
  apiBaseUrlAtom,
  isAuthLoadingAtom,
} from '../state/atoms'
import { isAuthenticatedSelector } from '../state/selectors'
import { login as apiLogin } from '../api/services'
import { apiClient } from '../api/client'
import { saveAuthSession, clearAuthSession } from '../storage'
import type { Branch, UserProfileData } from '../api/types'

interface AuthSession {
  token: string
  user: UserProfileData
  branches: Branch[]
  selectedBranchNid: string
  environment: 'development' | 'production'
  apiBaseUrl: string
}

export function useAuth() {
  const navigate = useNavigate()

  const [user, setUser] = useRecoilState(userAtom)
  const [token, setToken] = useRecoilState(authTokenAtom)
  const [branches, setBranches] = useRecoilState(branchesAtom)
  const [selectedBranch, setSelectedBranch] = useRecoilState(selectedBranchAtom)
  const environment = useRecoilValue(environmentAtom)
  const apiBaseUrl = useRecoilValue(apiBaseUrlAtom)
  const [isLoading] = useRecoilState(isAuthLoadingAtom)

  const isAuthenticated = useRecoilValue(isAuthenticatedSelector)

  // ============================================
  // Login
  // ============================================
  async function login(email: string, password: string): Promise<void> {
    try {
      console.log('Attempting login...')

      const { data, token: newToken } = await apiLogin(email, password)

      if (!data.branches || data.branches.length === 0) {
        throw new Error('No branches available for this user')
      }

      const firstBranch = data.branches[0]

      // Create session
      const session: AuthSession = {
        token: newToken,
        user: data.user,
        branches: data.branches,
        selectedBranchNid: firstBranch.nid,
        environment,
        apiBaseUrl,
      }

      // Save to storage (localStorage in browser)
      await saveAuthSession(session as any)

      // Synchronously set token in apiClient BEFORE Recoil state
      apiClient.setToken(newToken)

      // Now update Recoil state (effects will fire, but token already set)
      setToken(newToken)

      // Now safe to set user and branch state
      setUser(data.user)
      setBranches(data.branches)
      setSelectedBranch(firstBranch)

      console.log('✓ Login successful, navigating to home...')
      navigate('/', { replace: true })
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  // ============================================
  // Logout
  // ============================================
  async function logout(): Promise<void> {
    try {
      console.log('Logging out...')

      await clearAuthSession()

      // Synchronously clear apiClient token first
      apiClient.setToken(null)

      // Then clear Recoil state
      setUser(null)
      setBranches([])
      setSelectedBranch(null)
      setToken(null)

      console.log('✓ Logout successful, navigating to login...')
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  // ============================================
  // Set Selected Branch
  // ============================================
  function handleSetSelectedBranch(branch: Branch): void {
    setSelectedBranch(branch)

    // Update saved session
    if (user && isAuthenticated && token) {
      const session: AuthSession = {
        token,
        user,
        branches,
        selectedBranchNid: branch.nid,
        environment,
        apiBaseUrl,
      }

      saveAuthSession(session as any).catch((err) => {
        console.error('Failed to update selected branch:', err)
      })
    }
  }

  return {
    user,
    branches,
    selectedBranch,
    isAuthenticated,
    environment,
    apiBaseUrl,
    isLoading,
    login,
    logout,
    setSelectedBranch: handleSetSelectedBranch,
  }
}
